import { BASE_URL } from '../api/client'

/**
 * Lo que se rompe en el navegador no se queda en el navegador.
 *
 * POR QUÉ. Esto es una SPA: casi todo lo que puede salir mal, sale mal aquí. El
 * backend cuenta lo suyo —clase, mensaje y seis marcos a Loki, con reglas de
 * Grafana mirándolo— y del cliente no se sabía nada. Comprobado el 10-09-2026:
 * ni un `window.onerror`, ni un `unhandledrejection`, ni una frontera de error
 * en toda la aplicación.
 *
 * Y aquí un error de JS no degrada: corta. Un fotógrafo subiendo una boda de 400
 * RAW no ve un error, ve que la barra deja de moverse; cierra la pestaña y
 * vuelve mañana. El servidor respondió 200 a todo y no queda rastro.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Las tres defensas, que son lo que hace esto seguro de encender
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. **Se repite poco.** Un error dentro de un render no ocurre una vez, ocurre
 *    en bucle. Se recuerda la huella —mensaje más primer marco— y hay tope por
 *    sesión. Sin eso, una sola pestaña rota llena Loki.
 * 2. **No puede tirar la aplicación.** Todo dentro de `try` y nunca se espera.
 *    Un informador que revienta informando deja al usuario peor que el fallo.
 * 3. **`keepalive`.** Detrás de un error suele venir una navegación o el cierre
 *    de la pestaña, y una petición normal se cancela justo ahí.
 *
 * NO usa el cliente de la API de la casa a propósito: aquél lanza `ApiException`
 * en los errores, y lanzar desde el manejador de errores es la forma más directa
 * de convertir un fallo en un bucle. Aquí se va con `fetch` pelado.
 */

/** El tope por sesión. Más allá, lo que falta no son informes. */
const MAXIMO_POR_SESION = 5

const vistos = new Set<string>()
let enviados = 0

function recortar(valor: unknown, max: number): string {
  return typeof valor === 'string' ? valor.slice(0, max) : ''
}

/** Mensaje más primer marco: lo que separa dos fallos distintos de uno repetido. */
function huella(mensaje: string, pila: string): string {
  return `${mensaje}|${(pila.split('\n')[1] ?? '').trim()}`
}

/** Manda UN error si toca. No devuelve nada y no lanza nunca. */
export function reportarErrorDelNavegador(origen: string, error: unknown): void {
  try {
    if (enviados >= MAXIMO_POR_SESION) return

    const e = error instanceof Error ? error : undefined
    const mensaje = recortar(e ? `${e.name}: ${e.message}` : String(error), 300)
    const pila = recortar(e?.stack, 2000)

    const clave = huella(mensaje, pila)
    if (vistos.has(clave)) return
    vistos.add(clave)
    enviados++

    void fetch(`${BASE_URL}/public/errores-del-navegador`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        kind: recortar(origen, 40),
        message: mensaje,
        stack: pila,
        url: recortar(location.pathname + location.search, 300),
      }),
    }).catch(() => {
      /* Si no se puede informar, no se puede informar. No se reintenta: el
         reintento de un informe de error convierte un corte de red en tormenta. */
    })
  } catch {
    /* Ver la defensa 2. */
  }
}

/** Engancha los dos sitios por los que un error se escapa del navegador. */
export function instalarInformeDeErrores(): () => void {
  const enError = (ev: ErrorEvent) => reportarErrorDelNavegador('onerror', ev.error ?? ev.message)
  const enPromesa = (ev: PromiseRejectionEvent) =>
    reportarErrorDelNavegador('unhandledrejection', ev.reason)

  window.addEventListener('error', enError)
  window.addEventListener('unhandledrejection', enPromesa)

  return () => {
    window.removeEventListener('error', enError)
    window.removeEventListener('unhandledrejection', enPromesa)
  }
}
